import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CapacityReportStatus } from '@libs/services/graphql/generated/ala-backend.service';
import { EmptyBedReasonsDto } from '../../dtos-v2/empty-reasons.dto';
import { FacilityBedRowDto } from '../../dtos-v2/facility-bed-row.dto';
import { CapacityInputTransformerService } from '../../transformers-v2/capacity-input.transformer.service';
import { BedTypeCode } from '../../types-v2/bed-type-code';
import { CapacityService } from '../capacity.service';
import { BaseSignalStore, BaseState } from './base/base-signal-store.service';

@Injectable({ providedIn: 'root' })
export class FacilityEntryBedRowStateService extends BaseSignalStore<ICapacityBedRowState> {
  private readonly _capacityService = inject(CapacityService);
  private readonly _inputTransformerService = inject(
    CapacityInputTransformerService
  );
  constructor() {
    super({
      // Initial state
      selectedReportId: '',
      facilityBedsRows: [],
      bedRowValidation: [],
      currentStatus: CapacityReportStatus.NOTSTARTED,
      isLoading: false,
      error: '',
    });
  }

  //#region === SETTERS ====
  set setSelectedReportId(selectedReportId: string) {
    this.patchState({ selectedReportId });
  }
  set setSelectFacilityBedsRows(facilityBedsRows: FacilityBedRowDto[]) {
    this.patchState({ facilityBedsRows });
  }
  set setStatus(currentStatus: CapacityReportStatus) {
    this.patchState({ currentStatus });
  }
  set setBedRowValidation(rowValidation: IBedRowValidation) {
    const current = this.bedRowValidation() as IBedRowValidation[];
    if (!current) return;

    const updated = current.map((r) =>
      r.bedType === rowValidation.bedType ? rowValidation : r
    );

    this.patchState({ bedRowValidation: updated });
  }

  //#endregion === SETTERS ====

  //#region === SELECTORS ===
  readonly selectedReportId = this.select((s) => s.selectedReportId);
  readonly selectFacilityBedsRows = this.select((s) => s.facilityBedsRows);
  readonly currentStatus = this.select((s) => s.currentStatus);
  readonly bedRowValidation = this.select((s) => s.bedRowValidation);
  readonly allValidEntry = this.select((s) =>
    s.bedRowValidation.every((v) => v.isValid)
  );
  get facilityBedsRows$() {
    return toObservable(this.select((s) => s.facilityBedsRows));
  }
  // readonly areAllEntriesValid = this.select((s) =>
  //   this._validatorService.areAllEntriesValid(s.bedRowModelState)
  // );
  // isInitialDataExistSignal will only be false before the very first data are entered for the facility
  // readonly isInitialDataExistSignal = this.select((s) =>
  //   s.bedRowModelState.some((row) => row.operationalBeds || row.occupiedBeds)
  // );
  //#endregion  === SELECTORS ===

  //#region === ACTIONS ===
  initializeValidationState() {
    if (
      this.bedRowValidation().length === 0 &&
      this.selectFacilityBedsRows().length > 0
    ) {
      const bedRowValidation = [] as IBedRowValidation[];
      this.selectFacilityBedsRows().map((r) =>
        bedRowValidation.push({
          bedType: r.bedType,
          isValid: false,
        } as unknown as IBedRowValidation)
      );
      this.patchState({ bedRowValidation });
    }
  }
  resetToInitialState(): void {
    this.reset({
      // Initial state
      selectedReportId: '',
      facilityBedsRows: [],
      bedRowValidation: [],
      currentStatus: CapacityReportStatus.NOTSTARTED,
      isLoading: false,
      error: '',
    });
  }

  async updateHasEmptyBeds(bedType: BedTypeCode, hasEmptyBeds: boolean) {
    this.updateHasEmptyBedsState(bedType, hasEmptyBeds);

    const transformedInput =
      this._inputTransformerService.transformToHasEmptyBedsInput(
        bedType,
        hasEmptyBeds
      );

    await this._capacityService.updateCapacityReport(
      this.selectedReportId(),
      transformedInput
    );
  }

  updateHasEmptyBedsState(bedType: string, hasEmptyBeds: boolean) {
    const current = this.selectFacilityBedsRows() as FacilityBedRowDto[];
    if (!current) return;

    let [facilityBedsRow] = this.selectFacilityBedsRows().filter(
      (r) => r.bedType === bedType
    );
    if (!facilityBedsRow) return;

    facilityBedsRow = { ...facilityBedsRow, hasEmptyBeds: hasEmptyBeds };

    const updated = current.map((r) =>
      r.bedType === bedType ? facilityBedsRow : r
    );

    this.patchState({ facilityBedsRows: updated });
  }

  updateEmptyBedReason(
    bedType: string,
    reasonKey: keyof EmptyBedReasonsDto,
    newValue: number | null
  ) {
    this.updateState((state) => {
      const updatedRows = state.facilityBedsRows.map((row) => {
        if (row.bedType !== bedType || !row.emptyBedReasons) {
          return row;
        }

        // make a shallow clone of the reasons object
        const reasons = { ...row.emptyBedReasons };

        // safely read the current reason object
        const currentReason = reasons[reasonKey] ?? {
          current: null,
          previous: null,
        };

        // no change → return original
        if (Object.is(currentReason.current, newValue)) {
          return row;
        }

        // new updated reason object
        const updatedReason = {
          ...currentReason,
          current: newValue,
        };

        // write back
        reasons[reasonKey] = updatedReason;

        return {
          ...row,
          emptyBedReasons: reasons,
        };
      });

      // return full new state
      return {
        ...state,
        facilityBedsRows: updatedRows,
      };
    });
  }

  async saveEmptyBedReasons(
    bedType: string,
    reason: keyof EmptyBedReasonsDto,
    newValue: number | null
  ) {
    const transformedInput =
      this._inputTransformerService.transformToEmptyBedReasonInput(
        bedType as BedTypeCode,
        reason,
        newValue
      );
    await this._capacityService.updateCapacityReport(
      this.selectedReportId(),
      transformedInput
    );
  }

  // updateBadCountState(
  //   bedType: IBedType,
  //   countType: BedCountTypes,
  //   newCount: number | null
  // ) {
  //   const state = this.state();

  //   // find the target row
  //   const idx = state.bedRowModelState.findIndex((r) => r.bedType === bedType);
  //   if (idx === -1) return; // not found — nothing to update

  //   const row = state.bedRowModelState[idx];
  //   const bedState =
  //     countType === BedCountTypes.Operational
  //       ? row.operationalBeds
  //       : row.occupiedBeds;

  //   if (!bedState) return; // no data to update

  //   // if no change, skip patch
  //   if (bedState.currentBeds === newCount) return;

  //   // immutably update the nested bedState
  //   const updatedRow: IBedEntryRowModel = {
  //     ...row,
  //     [countType === BedCountTypes.Operational
  //       ? 'operationalBeds'
  //       : 'occupiedBeds']: {
  //       ...bedState,
  //       currentBeds: newCount,
  //     },
  //   };

  //   // immutably update the row array
  //   const updatedRows = [
  //     ...state.bedRowModelState.slice(0, idx),
  //     updatedRow,
  //     ...state.bedRowModelState.slice(idx + 1),
  //   ];

  //   // Update local state
  //   this.patchState({ bedRowModelState: updatedRows });
  // }

  // async updateBadCount(
  //   bedType: IBedType,
  //   countType: BedCountTypes,
  //   newCount: number | null
  // ) {
  //   this.updateBadCountState(bedType, countType, newCount);

  //   const eventArgs: BedCountChangeEventArgs = {
  //     changedFieldValue: newCount,
  //     changedFieldCoords: { countType, bedType },
  //   };

  //   const key = this._mapper.mapToBedCountFieldColumn(
  //     eventArgs.changedFieldCoords.bedType,
  //     eventArgs.changedFieldCoords.countType
  //   );

  //   // Save to backend
  //   await this.saveChangedValues([{ name: key, value: newCount }]);
  // }

  // async saveChangedValues(
  //   values: {
  //     name: keyof CapacityReportType;
  //     value: CapacityReportType[keyof CapacityReportType];
  //   }[]
  // ): Promise<void> {
  //   if (this.selectedFacilityId().length === 0) return Promise.resolve();

  //   const input = this._mapper.mapToUpdateCapacityReportInput(values);
  //   if (!input) return Promise.resolve();

  //   await this.updateCapacityReportByIdAsync(input);
  // }

  // async updateCapacityReportByIdAsync(
  //   input: UpdateCapacityReportInput
  // ): Promise<void> {
  //   return this.executeAsync(async () => {
  //     if (this.selectedFacilityId() && this.selectedFacilityId().length > 0) {
  //       await this._capacityService.updateCapacityReport(
  //         this.selectedFacilityId(),
  //         input
  //       );
  //     }
  //   });
  // }
  //#endregion  === ACTIONS ===
}

export interface ICapacityBedRowState extends BaseState {
  selectedReportId: string;
  facilityBedsRows: FacilityBedRowDto[];
  bedRowValidation: IBedRowValidation[];
  currentStatus: CapacityReportStatus;
}

export interface IBedRowValidation {
  bedType: string;
  isValid: boolean;
}
